import {
  ECardinality,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IDocumentParameterInput,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { ProjectSettingsService } from "../../../../services/project-settings-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { resolveLoadingPresentation } from "../../../../shared/utility/first-load-skeletons";
import { KuikaSkeletonForm, KuikaSkeletonRegion } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { SymbolPicker } from "./symbol-picker";

/**
 * Input bindings for the Resolve Document Parameters action (KOA-17254).
 *
 * This action has no declared parameters of its own. Its inputs are the inputs of whichever actions
 * the project's document parameter definitions reference — if `<SIRKET_ADI>` is supplied by
 * `GetSirketBilgi(@Id)`, then `@Id` is what needs binding here. So the panel:
 *
 *   1. reads the project's definitions (Project Settings → Document Parameters),
 *   2. collects the distinct inputs of the referenced actions, deduplicated per action so an input
 *      shared by six parameters is asked for once rather than six times,
 *   3. writes exactly that set onto the action instance and renders a SymbolPicker per input.
 *
 * Why the bindings live here and not in the project-level tool: symbols resolve against a screen.
 * `Directory.selectedRow.companyId`, screen inputs and other components' values only exist at a call
 * site, so a project-level panel could only offer bindings it cannot resolve. See
 * docs/plans/dms-document-parameters.md ("Karar 2").
 *
 * Parameter naming is `{ActionName}__{inputName}` — the action prefix keeps two actions that both take
 * an `@Id` from colliding into one binding.
 */

/**
 * Input bindings for the Resolve Document Parameters action (KOA-17254).
 *
 * This action has no declared parameters of its own. Its inputs are the project's **declared document inputs** —
 * the values the Document Parameters tool says come from outside. If `<SIRKET_ADI>` is supplied by
 * `GetSirketBilgi(@Id)` and that `@Id` is wired to a `UserId` input, then `UserId` is what needs binding here.
 *
 * <b>It names no document.</b> The action signs these inputs; resolution happens in the viewer, download and
 * materialize paths, each of which knows the document it is opening and resolves only the tags that document
 * contains. So this goes on page init once, and every document the screen touches is handled automatically —
 * earlier it had to be triggered per document, and a screen that forgot rendered raw tags.
 *
 * <b>Why the declared list and not the actions' parameters.</b> This panel used to derive its list by walking the
 * definitions to their supplying actions and asking for every parameter, named `{Action}__{input}`. Two actions
 * both needing a user id therefore asked for it twice, and adding a source action in the project-level tool
 * silently changed the shape of every call site. Declaring the inputs makes the contract explicit and lets several
 * actions share one binding — the same trade screen inputs make.
 *
 * Why the bindings live here and not in the project-level tool: symbols resolve against a screen.
 * `Directory.selectedRow.companyId`, screen inputs and other components' values only exist at a call site, so a
 * project-level tool could only offer bindings it cannot resolve. See docs/plans/dms-document-parameters.md
 * ("Karar 2").
 */

export interface IDocumentParametersSymbolsGroupState {
  inputs: IDocumentParameterInput[];
  isLoading: boolean;
  loadFailed: boolean;
}

export interface IDocumentParametersSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  changeActionParameter: (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => void;
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
  updateActionParametersForce: (newActionParameters: IActionParameter[], changeReason?: ESdChangeReason) => void;
}

class DocumentParametersSymbolsGroup extends PureSdComponent<
  IDocumentParametersSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IDocumentParametersSymbolsGroupState
> {
  constructor(props: IDocumentParametersSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = { inputs: [], isLoading: false, loadFailed: false };
  }

  componentDidMount(): void {
    this.loadInputs();
  }

  private loadInputs = (): void => {
    const projectId: string | undefined = this.props.sd?.project?.id;
    if (!projectId) return;

    this.setState({ isLoading: true });
    ProjectSettingsService.getDocumentParameters(projectId)
      .then((response) => {
        const inputs: IDocumentParameterInput[] = response?.data?.inputs ?? [];
        this.setState({ inputs, isLoading: false, loadFailed: false }, () => this.syncInstanceParameters(inputs));
      })
      .catch(() => {
        // A failed load must not wipe bindings the user already made, so leave the instance alone.
        this.setState({ isLoading: false, loadFailed: true });
      });
  };

  /** A parameter slot for a name that has no binding yet. */
  private emptyParameter = (name: string, typeName: string, cardinality: ECardinality): IActionParameter =>
    ({
      name,
      Typename: typeName,
      cardinality,
      symbol: {
        category: ESymbolCategory.fixed,
        fixedValue: undefined,
        label: "",
        description: name
      } as ISymbol
    }) as IActionParameter;

  /**
   * Brings the instance's parameter list in line with the declared inputs.
   *
   * Existing bindings are preserved by name — declaring another input must not reset the bindings already made.
   * Parameters for inputs that no longer exist are dropped; keeping them would emit dead arguments into generated
   * code and bind a value nothing reads.
   *
   * No document is bound here. The viewer, the download and the materialize paths each resolve for the document they
   * are opening, so this action supplies inputs once and never names a document.
   */
  private syncInstanceParameters = (inputs: IDocumentParameterInput[]): void => {
    const current: IActionParameter[] = this.props.parameters ?? [];

    const keep = (name: string, typeName: string, cardinality: ECardinality): IActionParameter =>
      current.find((candidate: IActionParameter) => candidate.name === name) ??
      this.emptyParameter(name, typeName, cardinality);

    const next: IActionParameter[] = inputs
      .filter((input: IDocumentParameterInput) => !!input?.name)
      .map((input: IDocumentParameterInput) =>
        keep(input.name, input.typeName ?? "String", input.cardinality ?? ECardinality.One)
      );

    const isUnchanged: boolean =
      next.length === current.length &&
      next.every((parameter: IActionParameter, index: number) => parameter.name === current[index]?.name);
    if (isUnchanged) return;

    this.props.updateActionParametersForce(next);
  };

  render() {
    const { inputs, isLoading, loadFailed } = this.state;
    const rows: IDocumentParameterInput[] = inputs.filter((input: IDocumentParameterInput) => !!input?.name);
    // The declared inputs are fetched as the panel mounts. Until they arrive there is nothing to bind,
    // so the rows are drawn in outline — a label over a field each — instead of dimming the panel.
    const isInputListPlaceholder: boolean = resolveLoadingPresentation(isLoading, rows.length > 0) === "skeleton";

    return (
      <div style={{ width: "100%" }}>
        {isInputListPlaceholder && (
          <KuikaSkeletonRegion label="Loading document inputs">
            <KuikaSkeletonForm fields={2} />
          </KuikaSkeletonRegion>
        )}

        {loadFailed && (
          <div className="kuika_add_actions_properties_name">
            Document parameter definitions could not be loaded. Existing bindings are unchanged.
          </div>
        )}

        {!isLoading && !loadFailed && rows.length === 0 && (
          <div className="kuika_add_actions_properties_name">
            This project declares no document inputs. Declare them in the Document Parameters tool in the header
            toolbar, wire the source actions to them there, then reopen this action.
          </div>
        )}

        {rows.map((input: IDocumentParameterInput) => {
          const parameter: IActionParameter | undefined = (this.props.parameters ?? []).find(
            (candidate: IActionParameter) => candidate.name === input.name
          );
          if (!parameter) return null;

          return (
            <div key={`documentParametersSymbolsGroup-${this.props.action.id}-${input.name}`}>
              <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                <ActionParameterTitle
                  parameter={{
                    name: input.name,
                    Typename: input.typeName ?? "String",
                    cardinality: input.cardinality ?? ECardinality.One
                  }}
                />
              </span>
              <SymbolPicker
                changeType="onBlur"
                action={this.props.action}
                symbol={parameter.symbol}
                valueType={parameter.Typename}
                trimValue={true}
                targetCardinality={parameter.cardinality}
                onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
                  this.props.changeActionParameter(parameter, symbol, changeReason)
                }
                bindingParameter={parameter}
              />
            </div>
          );
        })}
      </div>
    );
  }

  checkNeedRender = (
    changeReason: ESdChangeReason,
    nextProps: IDocumentParametersSymbolsGroupProps & IWithSDProps
  ): boolean => {
    if (!_.isEqual(nextProps.parameters, this.props.parameters)) {
      return true;
    }

    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools
    ) {
      return true;
    }

    return false;
  };
}

const documentParametersSymbolsGroup = withContext<IDocumentParametersSymbolsGroupProps>(
  withSD<IDocumentParametersSymbolsGroupProps>(DocumentParametersSymbolsGroup)
);
export { documentParametersSymbolsGroup as DocumentParametersSymbolsGroup };
